import SingleItem from 'src/layouts/SingleItem';
import {
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button
} from '@chakra-ui/react';
import { useSubmissionDetails } from 'src/pages/SubmissionDetails/SubmissionDetailsContext';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faChevronDown
} from '@fortawesome/pro-regular-svg-icons';
import { ButtonIcon } from '@morressier/alembic';
import { useLocation } from 'react-router-dom';
import IntegrityStatus from './IntegrityTab/IntegrityTab';
import UADTab from './UADTab';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import EmailVerification from './EmailVerification';

export const INTEGRITY_TABS = {
  INTEGRITY_STATUS: {
    label: 'Integrity checks',
    hasTooltip: false,
    tooltip: ''
  },
  UAD: {
    label: 'Anomaly Detection',
    hasTooltip: false,
    tooltip: ''
  },
  EMAIL_VERIFICATION: {
    label: 'Email Verification',
    hasTooltip: false,
    tooltip: ''
  }
} as const;

export type IIntegrityTab =
  (typeof INTEGRITY_TABS)[keyof typeof INTEGRITY_TABS];

type RenderTabContent = {
  activeTab: IIntegrityTab;
};

const renderTabContent = ({ activeTab }: RenderTabContent) => {
  switch (activeTab) {
    case INTEGRITY_TABS.INTEGRITY_STATUS:
      return <IntegrityStatus />;
    case INTEGRITY_TABS.UAD:
      return <UADTab />;
    case INTEGRITY_TABS.EMAIL_VERIFICATION:
      return <EmailVerification />;
    default:
      return null;
  }
};

export type BackofficeTabLabel =
  (typeof INTEGRITY_TABS)[keyof typeof INTEGRITY_TABS]['label'];

const IntegrityTab = () => {
  const { integritySubTab, setBackIntegritySubTab } = useSubmissionDetails();
  const { submissionContext } = useSubmissionContextData();
  const isUADEnable = submissionContext?.config.isUADEnable;
  const UADEnable = submissionContext?.config.uadChecks?.length;
  const location = useLocation();
  const filteredTabs = [
    INTEGRITY_TABS.INTEGRITY_STATUS,
    ...((isUADEnable && UADEnable) ? [INTEGRITY_TABS.UAD] : []),
    INTEGRITY_TABS.EMAIL_VERIFICATION
  ];

  const isValidTabLabel = (value: string): value is BackofficeTabLabel => {
    return Object.values(INTEGRITY_TABS).some(tab => tab.label === value);
  };

  const getTabByLabel = (label: BackofficeTabLabel) => {
    return Object.values(INTEGRITY_TABS).find(tab => tab.label === label);
  };
  const params = new URLSearchParams(location.search);
  const urlTab = params.get('activeSubTab');

  const defaultTab =
    urlTab && isValidTabLabel(urlTab) ? getTabByLabel(urlTab) : undefined;

  const activeTab: IIntegrityTab = filteredTabs.includes(integritySubTab)
    ? integritySubTab
    : defaultTab && filteredTabs.includes(defaultTab)
    ? defaultTab
    : filteredTabs[0] ?? INTEGRITY_TABS.INTEGRITY_STATUS;

  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleSubTabChange = (tab: IIntegrityTab) => {
    setBackIntegritySubTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('activeSubTab', tab.label);
    window.history.replaceState({}, '', `?${params.toString()}`);
  };
  const labels = filteredTabs?.map(({ label }) => label);
  const formattedLabels = labels?.join(', ')?.replace(/,([^,]*)$/, ' and$1');
  if (filteredTabs.length > 1) {
    return (
      <Flex flexDirection="column" gap={4}>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingSection">Integrity</Text>
          <Text textStyle="body">{formattedLabels}</Text>
        </Flex>
        <SingleItem.MainArea>
          <SingleItem.Navigation size="compact">
            {isMobile ? (
              <Menu>
                <MenuButton
                  as={Button}
                  variant="ghost"
                  rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
                  fontWeight="semibold"
                  size="sm"
                  px={4}
                >
                  {activeTab.label}
                </MenuButton>
                <MenuList zIndex={10}>
                  {filteredTabs.map(tab => (
                    <MenuItem
                      key={tab.label}
                      onClick={() => handleSubTabChange(tab)}
                      fontWeight={activeTab === tab ? 'semibold' : 'normal'}
                      bg={activeTab === tab ? 'blue.50' : undefined}
                    >
                      {tab.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            ) : (
              filteredTabs.map(tab => (
                <SingleItem.Tab
                  key={tab.label}
                  onClick={() => handleSubTabChange(tab)}
                  isActive={activeTab === tab}
                >
                  <Flex alignItems="center">
                    {tab.label}
                    {tab.hasTooltip && (
                      <Tooltip
                        hasArrow
                        shouldWrapChildren
                        placement="top"
                        label={tab.tooltip}
                        borderRadius={4}
                      >
                        <ButtonIcon
                          size="small"
                          ml={1}
                          ariaLabel="Help Icon"
                          icon={<FontAwesomeIcon icon={faInfoCircle} />}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                </SingleItem.Tab>
              ))
            )}
          </SingleItem.Navigation>

          <SingleItem.Content size={'small'} bgColor="white">
            {renderTabContent({
              activeTab
            })}
          </SingleItem.Content>
        </SingleItem.MainArea>
      </Flex>
    );
  }
  return (
    <>
      <IntegrityStatus />
    </>
  );
};

export default IntegrityTab;
